import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StatusPill, EmptyState } from '@ui-kit';
import { CommonUsagePanel } from '../../_shared/CommonUsagePanel';
import { useCurrentToken } from '../hooks/useCurrentToken';
import { useDecodeToken } from '../hooks/useDecodeToken';
import { useBlacklistStatus } from '../hooks/useBlacklistStatus';
import { useRefreshHistory } from '../hooks/useRefreshHistory';
import {
  decodeFormSchema,
  blacklistFormSchema,
  historyFormSchema,
  type DecodeFormValues,
  type BlacklistFormValues,
  type HistoryFormValues,
} from '../schema/tokenInspector.schema';
import type { DecodedTokenView, RefreshRotationItem } from '../types/tokenInspector.types';
import type { ReactNode } from 'react';
import type { CommonUsageItem } from '../../_shared/CommonUsagePanel';

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastItem {
  id: number;
  message: string;
  tone: 'success' | 'error';
}

let _toastCounter = 0;

function ToastContainer({ items, onRemove }: { items: ToastItem[]; onRemove: (id: number) => void }) {
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'fixed',
        bottom: 'var(--spacing-6)',
        right: 'var(--spacing-6)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-2)',
        zIndex: 2000,
        pointerEvents: 'none',
      }}
    >
      {items.map((item) => (
        <div
          key={item.id}
          role="status"
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            background: item.tone === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
            color: 'var(--color-text-inverse)',
            boxShadow: 'var(--shadow-md)',
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            minWidth: '220px',
            maxWidth: '400px',
          }}
        >
          <span style={{ flex: 1 }}>{item.message}</span>
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            aria-label="닫기"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-inverse)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-base)',
              padding: 0,
              lineHeight: 1,
              opacity: 0.8,
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [items, setItems] = useState<ToastItem[]>([]);

  const show = useCallback((message: string, tone: ToastItem['tone'] = 'success') => {
    const id = ++_toastCounter;
    setItems((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => {
      setItems((prev) => prev.filter((i) => i.id !== id));
    }, 4000);
  }, []);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  return { items, show, remove };
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────

function FieldTooltip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <button
        type="button"
        aria-label={`설명: ${text}`}
        tabIndex={0}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '14px',
          height: '14px',
          borderRadius: '50%',
          border: '1px solid var(--color-text-muted)',
          background: 'transparent',
          color: 'var(--color-text-muted)',
          fontSize: '10px',
          fontWeight: 700,
          lineHeight: 1,
          cursor: 'help',
          padding: 0,
          marginLeft: 'var(--spacing-1)',
          flexShrink: 0,
        }}
      >
        ?
      </button>
      {visible && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: '120%',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            whiteSpace: 'nowrap',
            boxShadow: 'var(--shadow-md)',
            zIndex: 100,
            maxWidth: '280px',
            whiteSpaceCollapse: 'collapse',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

// ─── InfoCard ────────────────────────────────────────────────────────────────

function InfoCard({ children }: { children: ReactNode }) {
  return (
    <div
      role="note"
      style={{
        background: 'color-mix(in srgb, var(--color-brand-primary) 6%, var(--color-surface-muted))',
        border: '1px solid color-mix(in srgb, var(--color-brand-primary) 30%, var(--color-surface-border))',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-secondary)',
        lineHeight: 1.6,
        display: 'flex',
        gap: 'var(--spacing-3)',
        alignItems: 'flex-start',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: 'var(--font-size-base)',
          color: 'var(--color-brand-primary)',
          flexShrink: 0,
          marginTop: '2px',
        }}
      >
        ℹ
      </span>
      <div>{children}</div>
    </div>
  );
}

// ─── DescriptionNote ─────────────────────────────────────────────────────────

function DescriptionNote({ children }: { children: ReactNode }) {
  return (
    <p
      style={{
        margin: '0 0 var(--spacing-3)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-muted)',
        lineHeight: 1.6,
        borderLeft: '3px solid var(--color-surface-border)',
        paddingLeft: 'var(--spacing-3)',
      }}
    >
      {children}
    </p>
  );
}

// ─── Common Usage Items (§6.7.1 — 14 rows) ───────────────────────────────────

const COMMON_USAGE_ITEMS: CommonUsageItem[] = [
  { module: 'eraf-core', moduleColor: 'blue', api: 'ApiResponse<T> (C002)', role: '4 endpoint 모든 응답 표준 wrapper', snippet: 'return ApiResponse.success(service.decode(req));' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'ErrorResponse · MetaInfo (C002)', role: '403/400 본문 (FE 매핑)', snippet: 'if (!res.success) showError(res.error.code);' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'BusinessException · CommonErrorCode.FORBIDDEN (C001)', role: 'ROLE_ADMIN 외 접근 시 throw', snippet: 'throw new ForbiddenException(CommonErrorCode.FORBIDDEN);' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'GlobalExceptionHandler (C001)', role: '컨트롤러 advice (자동)', snippet: '// 자동 적용 — biz 코드 없음' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'RequestContextHolder.current().traceId() (C006/C007)', role: '모든 응답 traceId 동봉 + audit 로그 상관관계', snippet: 'String tid = RequestContextHolder.current().traceId();' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'StringUtils.mask(...) (C004)', role: 'maskedToken 생성 (앞 6 + 끝 3)', snippet: 'StringUtils.mask(token, 6, 3, \'*\')' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'DateUtils.relative(Instant, Locale) (C004)', role: '"10분 전" 만료 표시', snippet: 'DateUtils.relative(claims.expiresAt(), locale)' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtAuthenticationService.verify(token) (C028)', role: '임의 토큰 디코드 + 서명·만료 검증', snippet: 'TokenClaims c = jwt.verify(req.token());' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'TokenClaims (C028)', role: '6 영역 그리드의 데이터 구조', snippet: 'record TokenClaims(Long userId, Set<String> roles, ...)' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtAuthenticationToken.getClaims() (C028)', role: 'GET /current — @AuthenticationPrincipal 추출', snippet: 'TokenClaims c = (TokenClaims) auth.getPrincipal();' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtTokenBlacklist.isBlacklisted(jti) (C028)', role: '패널 C dual-store 조회 (Redis primary + JPA fallback)', snippet: 'boolean hit = blacklist.isBlacklisted(jti);' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtAuthenticationFilter (C028)', role: 'Authorization: Bearer 헤더 자동 처리', snippet: '// 필터 자동 적용 — biz 코드 없음' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'SecurityChainCustomizer · PathPolicy.hasRole (C029)', role: '/api/eraf/dev/** ADMIN 전용 게이트', snippet: 'PathPolicy.hasRole("/api/eraf/dev/**", "ROLE_ADMIN")' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'DynamicPermissionService.permissionsOf(userId) (C030)', role: 'claim vs DB permissions diff', snippet: 'Set<String> db = perm.permissionsOf(userId);' },
];

// ─── Shared helpers ────────────────────────────────────────────────────────────

function PanelShell({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return (
    <article
      style={{
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-elevated)',
          borderBottom: '1px solid var(--color-surface-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
        }}
      >
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
          {title}
        </span>
        {sub && (
          <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-mono)' }}>
            {sub}
          </span>
        )}
      </div>
      <div style={{ padding: 'var(--spacing-4)', flex: 1 }}>{children}</div>
    </article>
  );
}

function ResultRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-2)',
        padding: 'var(--spacing-1) 0',
        borderBottom: '1px solid var(--color-surface-border)',
        fontSize: 'var(--font-size-xs)',
      }}
    >
      <span
        style={{
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-xs)',
          minWidth: '110px',
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-mono)', flex: 1, wordBreak: 'break-all' }}>
        {children}
      </span>
    </div>
  );
}

function SkeletonCard() {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" aria-label={t('tokenInspector.loading')}>
      {[80, 60, 40, 70].map((w, i) => (
        <div
          key={i}
          style={{
            height: '12px',
            width: `${w}%`,
            background: 'linear-gradient(90deg, var(--color-surface-elevated) 25%, var(--color-surface-muted) 50%, var(--color-surface-elevated) 75%)',
            backgroundSize: '200% 100%',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 'var(--spacing-2)',
            animation: 'skeleton-shimmer 1.5s infinite',
          }}
        />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
        border: '1px solid var(--color-danger)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-danger)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-2)',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 'var(--font-size-base)' }}>!</span>
      <span>{message}</span>
    </div>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--spacing-6)',
        textAlign: 'center',
        color: 'var(--color-text-muted)',
        fontSize: 'var(--font-size-sm)',
        background: 'var(--color-surface-elevated)',
        border: '1px dashed var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {children}
    </div>
  );
}

// ─── JSON Syntax Highlight ─────────────────────────────────────────────────────

function JsonHighlight({ value }: { value: unknown }) {
  const json = JSON.stringify(value, null, 2);

  const highlighted = json.replace(
    /("(?:[^"\\]|\\.)*")(\s*:)?|(\btrue\b|\bfalse\b)|\bnull\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],])/g,
    (match, strKey, colon, bool, num, punct) => {
      if (strKey && colon) {
        return `<span style="color:var(--color-syntax-key)">${strKey}</span>${colon}`;
      }
      if (strKey) {
        return `<span style="color:var(--color-syntax-string)">${strKey}</span>`;
      }
      if (bool !== undefined) {
        return `<span style="color:var(--color-syntax-bool)">${bool}</span>`;
      }
      if (match === 'null') {
        return `<span style="color:var(--color-syntax-null);font-style:italic">null</span>`;
      }
      if (num !== undefined) {
        return `<span style="color:var(--color-syntax-number)">${num}</span>`;
      }
      if (punct) {
        return `<span style="color:var(--color-text-muted)">${punct}</span>`;
      }
      return match;
    }
  );

  return (
    <pre
      style={{
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--spacing-3)',
        fontFamily: 'var(--font-family-mono)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-primary)',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        overflowX: 'auto',
        margin: 0,
      }}
      // JSON is built from server-side data only; no user-controlled HTML.
      dangerouslySetInnerHTML={{ __html: highlighted }}
    />
  );
}

// ─── TTL Countdown ─────────────────────────────────────────────────────────────

function TtlCountdown({ initialSeconds }: { initialSeconds: number }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(initialSeconds);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // R7-038 fix: exp claim 없으면 initialSeconds 가 NaN/Infinity 일 수 있다.
  // isFinite 체크로 비정상 값을 조기 차단한다.
  const isValidSeconds = isFinite(initialSeconds) && !isNaN(initialSeconds);

  useEffect(() => {
    if (!isValidSeconds) return;
    setRemaining(initialSeconds);
    intervalRef.current = setInterval(() => {
      setRemaining((s) => s - 1);
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [initialSeconds, isValidSeconds]);

  // R7-038: exp 없는 위조 토큰 → 친화 라벨 표시
  if (!isValidSeconds) {
    return (
      <div style={{ marginTop: 'var(--spacing-2)' }}>
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            color: 'var(--color-text-muted)',
            display: 'inline-block',
          }}
          aria-label={t('tokenInspector.expiry.unknown')}
        >
          {t('tokenInspector.expiry.unknown')}
        </span>
      </div>
    );
  }

  const abs = Math.abs(remaining);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  const formatted = `${h > 0 ? `${h}h ` : ''}${m}m ${s}s`;
  const isExpired = remaining <= 0;

  return (
    <div style={{ marginTop: 'var(--spacing-2)' }}>
      <span
        style={{
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-xl)',
          fontWeight: 700,
          color: isExpired ? 'var(--color-danger)' : remaining < 300 ? 'var(--color-warning)' : 'var(--color-success)',
          display: 'inline-block',
        }}
        aria-live="polite"
        aria-label={`${t('tokenInspector.panel.current.expiresIn')}: ${formatted}`}
      >
        {isExpired ? '-' : ''}{formatted}
      </span>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginLeft: 'var(--spacing-2)' }}>
        {isExpired ? t('tokenInspector.status.expired') : t('tokenInspector.ttl.remaining')}
      </span>
    </div>
  );
}

// ─── Decoded Token Display (shared between Panel A and Panel B result) ─────────

interface DecodedViewProps {
  data: DecodedTokenView;
  untrusted?: boolean;
}

// ─── JWT field tooltip definitions ───────────────────────────────────────────

const HEADER_TOOLTIPS: Record<string, string> = {
  alg: 'alg=HS384: 서명 알고리즘 (HMAC-SHA384, ERAF 표준)',
  typ: 'typ=JWT: 토큰 타입 (고정)',
  kid: 'kid: key id — 시크릿 회전 시 서명 키 식별',
};

const PAYLOAD_TOOLTIPS: Record<string, string> = {
  sub: 'sub: 토큰 주체 (userId)',
  iss: 'iss: 발급자 (eraf-auth-server)',
  aud: 'aud: 수신자 (eraf-api)',
  exp: 'exp: 만료 시각 (Unix epoch)',
  iat: 'iat: 발급 시각 (Unix epoch)',
  nbf: 'nbf: not-before — 이 시각 이전에는 유효하지 않음',
  jti: 'jti: 토큰 고유 id — 블랙리스트 키로 사용',
  roles: 'roles: 발급 시점 사용자 역할 (캐시 정보)',
  permissions: 'permissions: 발급 시점 권한 목록 (캐시 정보)',
};

// ─── JSON section header with tooltips ───────────────────────────────────────

function JsonSectionLabel({
  label,
  tooltipMap,
  value,
}: {
  label: string;
  tooltipMap: Record<string, string>;
  value: unknown;
}) {
  const keys = value != null && typeof value === 'object' ? Object.keys(value as object) : [];
  return (
    <div style={{ marginBottom: 'var(--spacing-2)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          flexWrap: 'wrap',
          marginBottom: 'var(--spacing-1)',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            fontSize: '10px',
            fontWeight: 700,
            padding: '1px var(--spacing-2)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface-muted)',
            color: 'var(--color-text-muted)',
            letterSpacing: '0.06em',
          }}
        >
          {label}
        </span>
        {keys.filter((k) => tooltipMap[k]).map((k) => (
          <span
            key={k}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            {k}
            <FieldTooltip text={tooltipMap[k]} />
          </span>
        ))}
      </div>
    </div>
  );
}

function DecodedView({ data, untrusted }: DecodedViewProps) {
  const { t } = useTranslation();
  return (
    <div>
      {/* Invalid signature banner */}
      {!data.signatureValid && (
        <div
          role="alert"
          style={{
            background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            marginBottom: 'var(--spacing-3)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-danger)',
            fontWeight: 600,
          }}
        >
          {t('tokenInspector.panel.decode.invalidSignature')}
        </div>
      )}

      {/* Status Pills row */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', marginBottom: 'var(--spacing-3)' }}>
        <StatusPill tone={data.signatureValid ? 'success' : 'danger'}>
          {data.signatureValid ? t('tokenInspector.status.signatureValid') : t('tokenInspector.status.signatureInvalid')}
        </StatusPill>
        <StatusPill tone={data.expired ? 'danger' : 'success'}>
          {data.expired ? t('tokenInspector.status.expired') : t('tokenInspector.status.notExpired')}
        </StatusPill>
        {data.blacklisted !== null && (
          <StatusPill tone={data.blacklisted ? 'danger' : 'success'}>
            {data.blacklisted ? t('tokenInspector.status.blacklisted') : t('tokenInspector.status.notBlacklisted')}
          </StatusPill>
        )}
      </div>

      {/* Masked token */}
      <ResultRow label="maskedToken">
        <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {data.maskedToken}
        </code>
      </ResultRow>

      {/* algorithm */}
      <ResultRow label="algorithm">
        <StatusPill tone="info">{data.signatureMeta.algorithm}</StatusPill>
      </ResultRow>

      {/* issuer */}
      {data.signatureMeta.issuer && (
        <ResultRow label="issuer">
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-xs)' }}>{data.signatureMeta.issuer}</span>
        </ResultRow>
      )}

      {/* remainingTtl */}
      {data.remainingTtlSeconds !== null && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-1)' }}>{t('tokenInspector.panel.current.expiresIn')}</div>
          <TtlCountdown initialSeconds={data.remainingTtlSeconds} />
        </div>
      )}

      {/* Header JSON with tooltips */}
      <div style={{ marginBottom: 'var(--spacing-3)' }}>
        <JsonSectionLabel label="HEADER" tooltipMap={HEADER_TOOLTIPS} value={data.header} />
        <JsonHighlight value={data.header} />
      </div>

      {/* Payload JSON with tooltips — greyed out if untrusted */}
      <div>
        <JsonSectionLabel label="PAYLOAD" tooltipMap={PAYLOAD_TOOLTIPS} value={data.payload} />
        <div
          style={{
            position: 'relative',
            ...(untrusted || !data.signatureValid ? { filter: 'grayscale(0.6)', opacity: 0.6 } : {}),
          }}
        >
          <JsonHighlight value={data.payload} />
          {(untrusted || !data.signatureValid) && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-12deg)',
                fontFamily: 'var(--font-family-mono)',
                fontWeight: 800,
                fontSize: 'var(--font-size-xl)',
                color: 'var(--color-danger)',
                border: '3px solid var(--color-danger)',
                padding: 'var(--spacing-1) var(--spacing-3)',
                borderRadius: 'var(--radius-sm)',
                background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
                pointerEvents: 'none',
                opacity: 0.85,
              }}
            >
              UNTRUSTED
            </div>
          )}
        </div>
      </div>

      {/* DB Permissions diff */}
      {data.claimVsDbDiff && data.claimVsDbDiff.length > 0 && (
        <div style={{ marginTop: 'var(--spacing-3)' }}>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>
            {t('tokenInspector.diff.differ')}
          </div>
          <DescriptionNote>{t('tokenInspector.diff.description')}</DescriptionNote>
          <div style={{ display: 'flex', gap: 'var(--spacing-1)', flexWrap: 'wrap' }}>
            {data.claimVsDbDiff.map((p) => (
              <StatusPill key={p} tone="warning">{p}</StatusPill>
            ))}
          </div>
        </div>
      )}
      {data.claimVsDbDiff && data.claimVsDbDiff.length === 0 && data.dbPermissions && data.dbPermissions.length > 0 && (
        <div style={{ marginTop: 'var(--spacing-3)' }}>
          <StatusPill tone="success">{t('tokenInspector.diff.equal')}</StatusPill>
        </div>
      )}
    </div>
  );
}

// ─── Panel A: Current Token ────────────────────────────────────────────────────

interface PanelAProps {
  onCopyRequest: (token: string) => void;
}

function PanelA({ onCopyRequest }: PanelAProps) {
  const { t } = useTranslation();
  const { data, isFetching, isError, error, refetch } = useCurrentToken();

  return (
    <PanelShell title={t('tokenInspector.panel.current.title')} sub="GET /current">
      {isFetching && <SkeletonCard />}
      {isError && !isFetching && (
        <div>
          <ErrorBanner message={(error as { message?: string })?.message ?? t('tokenInspector.error.fetchFailed')} />
          <button
            type="button"
            onClick={() => void refetch()}
            style={{
              marginTop: 'var(--spacing-3)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('tokenInspector.panel.current.retry')}
          </button>
        </div>
      )}
      {data && !isFetching && (
        <div>
          <DecodedView data={data} />
          <button
            type="button"
            onClick={() => onCopyRequest(data.maskedToken)}
            style={{
              marginTop: 'var(--spacing-3)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-xs)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
            aria-label={t('tokenInspector.copyToken')}
          >
            {t('tokenInspector.copyToken')}
          </button>
        </div>
      )}
      {!data && !isFetching && !isError && (
        <div>
          <StatusPill tone="neutral">idle</StatusPill>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-3)' }}>
            {t('tokenInspector.panel.current.fetchIdle')}
          </p>
        </div>
      )}
    </PanelShell>
  );
}

// ─── Panel B: Decode Arbitrary Token ─────────────────────────────────────────

function PanelB() {
  const { t } = useTranslation();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<DecodeFormValues>({
    resolver: zodResolver(decodeFormSchema),
  });

  const { mutate, data, isPending, isError, error, reset: resetMutation } = useDecodeToken();

  const onSubmit = useCallback(
    (values: DecodeFormValues) => {
      mutate(values.token);
    },
    [mutate]
  );

  const handleClear = useCallback(() => {
    reset();
    resetMutation();
  }, [reset, resetMutation]);

  const textareaStyle: React.CSSProperties = {
    width: '100%',
    minHeight: '96px',
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: `1px solid ${errors.token ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-mono)',
    resize: 'vertical',
    lineHeight: 1.4,
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <PanelShell title={t('tokenInspector.panel.decode.title')} sub="POST /decode">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <textarea
          id="decode-token-input"
          placeholder={t('tokenInspector.panel.decode.placeholder')}
          maxLength={4096}
          aria-label={t('tokenInspector.panel.decode.placeholder')}
          aria-invalid={!!errors.token}
          aria-describedby={errors.token ? 'decode-token-error' : undefined}
          {...register('token')}
          style={textareaStyle}
        />
        {errors.token && (
          <span
            id="decode-token-error"
            role="alert"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', display: 'block', marginBottom: 'var(--spacing-2)' }}
          >
            {errors.token.message}
          </span>
        )}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)' }}>
          <button
            type="submit"
            disabled={isPending}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: isPending ? 0.7 : 1,
            }}
          >
            {isPending ? t('tokenInspector.panel.decode.decoding') : t('tokenInspector.panel.decode.button')}
          </button>
          <button
            type="button"
            onClick={handleClear}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('tokenInspector.panel.decode.clear')}
          </button>
        </div>
      </form>

      <div style={{ marginTop: 'var(--spacing-4)' }}>
        {isPending && <SkeletonCard />}
        {isError && !isPending && (
          <ErrorBanner message={(error as { message?: string })?.message ?? t('tokenInspector.panel.decode.decodeFailed')} />
        )}
        {data && !isPending && <DecodedView data={data} untrusted={!data.signatureValid} />}
        {!data && !isPending && !isError && (
          <EmptyHint>{t('tokenInspector.panel.decode.emptyHint')}</EmptyHint>
        )}
      </div>
    </PanelShell>
  );
}

// ─── Panel C: Blacklist Dual-Store ────────────────────────────────────────────

function PanelC() {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors } } = useForm<BlacklistFormValues>({
    resolver: zodResolver(blacklistFormSchema),
  });

  const [submittedJti, setSubmittedJti] = useState('');
  const [fetchEnabled, setFetchEnabled] = useState(false);

  const { data, isFetching, isError, error } = useBlacklistStatus(submittedJti, fetchEnabled);

  const onSubmit = useCallback((values: BlacklistFormValues) => {
    setFetchEnabled(false);
    setSubmittedJti(values.jti);
    requestAnimationFrame(() => setFetchEnabled(true));
  }, []);

  const deriveSource = () => {
    if (!data) return null;
    if (data.redisHit) return 'REDIS-PRIMARY';
    if (data.jpaHit) return 'JPA-FALLBACK';
    return 'NOT-FOUND';
  };
  const source = deriveSource();

  return (
    <PanelShell title={t('tokenInspector.panel.blacklist.title')} sub="GET /blacklist">
      <DescriptionNote>{t('tokenInspector.panel.blacklist.description')}</DescriptionNote>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
          <input
            type="text"
            placeholder={t('tokenInspector.panel.blacklist.jtiLabel')}
            autoComplete="off"
            aria-label={t('tokenInspector.panel.blacklist.jtiLabel')}
            aria-invalid={!!errors.jti}
            aria-describedby={errors.jti ? 'blacklist-jti-error' : undefined}
            {...register('jti')}
            style={{
              flex: 1,
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-surface-elevated)',
              border: `1px solid ${errors.jti ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-mono)',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            disabled={isFetching}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isFetching ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              whiteSpace: 'nowrap',
              opacity: isFetching ? 0.7 : 1,
            }}
          >
            {isFetching ? t('tokenInspector.panel.blacklist.querying') : t('tokenInspector.panel.blacklist.button')}
          </button>
        </div>
        {errors.jti && (
          <span id="blacklist-jti-error" role="alert" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', display: 'block', marginBottom: 'var(--spacing-2)' }}>
            {errors.jti.message}
          </span>
        )}
      </form>

      {isFetching && <SkeletonCard />}
      {isError && !isFetching && (
        <ErrorBanner message={(error as { message?: string })?.message ?? t('tokenInspector.panel.blacklist.queryFailed')} />
      )}
      {data && !isFetching && (
        <div
          style={{
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-3)',
            marginTop: 'var(--spacing-2)',
          }}
          role="region"
          aria-label={t('tokenInspector.panel.blacklist.resultAriaLabel')}
        >
          {/* dual-store pills */}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap' }}>
            <StatusPill tone={data.redisHit ? 'success' : 'neutral'}>
              Redis {data.redisHit ? '적중' : '미적중'}
            </StatusPill>
            <StatusPill tone={data.jpaHit ? 'warning' : 'neutral'}>
              JPA {data.jpaHit ? '적중' : '미적중'}
            </StatusPill>
            {source && (
              <StatusPill tone={source === 'REDIS-PRIMARY' ? 'success' : source === 'JPA-FALLBACK' ? 'warning' : 'neutral'}>
                {source}
              </StatusPill>
            )}
          </div>
          <ResultRow label="jti">
            <code style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}>{data.jti}</code>
          </ResultRow>
          {data.revokedAt && (
            <ResultRow label="revokedAt">
              <span style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)' }}>{data.revokedAt}</span>
            </ResultRow>
          )}
          {data.revokedReason && (
            <ResultRow label="revokedReason">
              <StatusPill tone={data.revokedReason === 'LOGOUT' ? 'danger' : data.revokedReason === 'ROTATION' ? 'info' : 'warning'}>
                {data.revokedReason}
              </StatusPill>
            </ResultRow>
          )}
          {data.expiresAt && (
            <ResultRow label="expiresAt">
              <span style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)' }}>{data.expiresAt}</span>
            </ResultRow>
          )}
          {!data.redisHit && !data.jpaHit && (
            <div style={{ marginTop: 'var(--spacing-2)' }}>
              <StatusPill tone="success">{t('tokenInspector.panel.blacklist.validToken')}</StatusPill>
            </div>
          )}
        </div>
      )}
      {!data && !isFetching && !isError && (
        <EmptyHint>{t('tokenInspector.panel.blacklist.notFound')}</EmptyHint>
      )}
    </PanelShell>
  );
}

// ─── Panel D: Refresh Rotation History ────────────────────────────────────────

interface ChainNodeProps {
  item: RefreshRotationItem;
  isLast: boolean;
  isPrev: boolean;
  isNew: boolean;
}

function ChainNode({ item, isLast, isPrev, isNew }: ChainNodeProps) {
  const statusColor: Record<string, string> = {
    ACTIVE:  'var(--color-success)',
    ROTATED: 'var(--color-info)',
    REVOKED: 'var(--color-danger)',
    EXPIRED: 'var(--color-text-muted)',
  };
  const color = statusColor[item.status] ?? 'var(--color-text-muted)';
  const opacity = item.status === 'REVOKED' || item.status === 'EXPIRED' ? 0.7 : 1;

  return (
    <>
      <div
        role="listitem"
        style={{
          background: 'var(--color-surface-elevated)',
          border: `1px solid ${color}`,
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-2) var(--spacing-3)',
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-xs)',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          minWidth: '90px',
          opacity,
        }}
        aria-label={`${item.jti} (${item.status})`}
      >
        {/* #383: data-testid — 첫 항목(이전 토큰)=prev, 마지막 항목(새 토큰)=new */}
        <span
          style={{ fontWeight: 700, color, fontSize: '10px', wordBreak: 'break-all' }}
          data-testid={isPrev ? 'token-rotation-prev-jti' : isNew ? 'token-rotation-new-jti' : undefined}
        >
          {item.jti.length > 16 ? `${item.jti.slice(0, 8)}...` : item.jti}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>
          {item.status}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>
          {item.issuedAt.slice(0, 10)}
        </span>
      </div>
      {/* #383: 화살표 방향 → (이전→새, 시간축 LTR). 마지막 노드 다음엔 화살표 없음 */}
      {!isLast && (
        <span
          aria-hidden="true"
          data-testid="token-rotation-arrow"
          style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-base)' }}
        >
          →
        </span>
      )}
    </>
  );
}

function PanelD() {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors } } = useForm<HistoryFormValues>({
    resolver: zodResolver(historyFormSchema),
  });

  const [submittedUserId, setSubmittedUserId] = useState(0);
  const [fetchEnabled, setFetchEnabled] = useState(false);

  const { data, isFetching, isError, error } = useRefreshHistory(submittedUserId, fetchEnabled);

  const onSubmit = useCallback((values: HistoryFormValues) => {
    setFetchEnabled(false);
    setSubmittedUserId(Number(values.userId));
    requestAnimationFrame(() => setFetchEnabled(true));
  }, []);

  return (
    <PanelShell title={t('tokenInspector.panel.refresh.title')} sub="GET /refresh-history">
      <DescriptionNote>{t('tokenInspector.panel.refresh.description')}</DescriptionNote>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
          <input
            type="text"
            inputMode="numeric"
            placeholder={t('tokenInspector.panel.refresh.userIdLabel')}
            autoComplete="off"
            aria-label={t('tokenInspector.panel.refresh.userIdLabel')}
            aria-invalid={!!errors.userId}
            aria-describedby={errors.userId ? 'history-userId-error' : undefined}
            {...register('userId')}
            style={{
              flex: 1,
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-surface-elevated)',
              border: `1px solid ${errors.userId ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-mono)',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            disabled={isFetching}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isFetching ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              whiteSpace: 'nowrap',
              opacity: isFetching ? 0.7 : 1,
            }}
          >
            {isFetching ? t('tokenInspector.panel.refresh.querying') : t('tokenInspector.panel.refresh.button')}
          </button>
        </div>
        {errors.userId && (
          <span id="history-userId-error" role="alert" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', display: 'block', marginBottom: 'var(--spacing-2)' }}>
            {errors.userId.message}
          </span>
        )}
      </form>

      {isFetching && <SkeletonCard />}
      {isError && !isFetching && (
        <ErrorBanner message={(error as { message?: string })?.message ?? t('tokenInspector.panel.refresh.queryFailed')} />
      )}
      {data && !isFetching && data.items.length === 0 && (
        <EmptyState
          title={t('tokenInspector.panel.refresh.empty')}
          description={t('tokenInspector.panel.refresh.emptyDescription')}
          action={
            <button
              type="button"
              onClick={() => setFetchEnabled(false)}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('tokenInspector.panel.refresh.queryOther')}
            </button>
          }
        />
      )}
      {data && !isFetching && data.items.length > 0 && (
        <div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-2)' }}>
            {t('tokenInspector.panel.refresh.userCount', { userId: data.userId, count: data.items.length })}
          </div>
          {/* #383: 서버는 DESC(최신 위) 반환 → 화면 chain 은 ASC(이전→새, LTR 시간축) 로 반전 표시 */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap', marginTop: 'var(--spacing-2)' }}
            role="list"
            aria-label={t('tokenInspector.panel.refresh.chainAriaLabel')}
          >
            {[...data.items].reverse().map((item, idx, arr) => (
              <ChainNode
                key={item.jti}
                item={item}
                isLast={idx === arr.length - 1}
                isPrev={idx === 0}
                isNew={idx === arr.length - 1}
              />
            ))}
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', marginTop: 'var(--spacing-3)' }}>
            <StatusPill tone="success">ACTIVE</StatusPill>
            <StatusPill tone="info">ROTATED</StatusPill>
            <StatusPill tone="danger">REVOKED</StatusPill>
            <StatusPill tone="neutral">EXPIRED</StatusPill>
          </div>
        </div>
      )}
      {!data && !isFetching && !isError && (
        <EmptyHint>{t('tokenInspector.panel.refresh.notFound')}</EmptyHint>
      )}
    </PanelShell>
  );
}

// ─── Copy Token Dialog ─────────────────────────────────────────────────────────

interface CopyDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

function CopyTokenDialog({ open, onClose, onConfirm }: CopyDialogProps) {
  const [confirming, setConfirming] = useState(false);
  const { t } = useTranslation();
  // Keyboard: Esc → close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="copy-dialog-title"
      aria-describedby="copy-dialog-desc"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay-backdrop)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          minWidth: '400px',
          maxWidth: '560px',
          boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--color-border-subtle)',
        }}
      >
        <h2
          id="copy-dialog-title"
          style={{ margin: 0, fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}
        >
          {t('tokenInspector.dialog.title')}
        </h2>
        <p
          id="copy-dialog-desc"
          style={{ marginTop: 'var(--spacing-4)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}
        >
          {t('tokenInspector.dialog.copyWarn')}
        </p>
        <div
          style={{
            marginTop: 'var(--spacing-6)',
            display: 'flex',
            gap: 'var(--spacing-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('tokenInspector.dialog.cancel')}
          </button>
          <button
            type="button"
            disabled={confirming}
            onClick={() => {
              setConfirming(true);
              void onConfirm().finally(() => setConfirming(false));
            }}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-danger)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: confirming ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: confirming ? 0.7 : 1,
            }}
          >
            {confirming ? '...' : t('tokenInspector.dialog.confirmCopy')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inner page content ────────────────────────────────────────────────────────

function TokenInspectorPageContent() {
  const { t } = useTranslation();
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [pendingCopyToken, setPendingCopyToken] = useState('');
  const [fallbackToken, setFallbackToken] = useState<string | null>(null);
  const { refetch: refetchCurrent } = useCurrentToken();
  const { items: toastItems, show: showToast, remove: removeToast } = useToast();

  const handleCopyRequest = useCallback((token: string) => {
    setPendingCopyToken(token);
    setCopyDialogOpen(true);
  }, []);

  const handleCopyConfirm = useCallback(async () => {
    const rawToken =
      localStorage.getItem('access_token') ??
      sessionStorage.getItem('access_token') ??
      (pendingCopyToken && !pendingCopyToken.includes('*') ? pendingCopyToken : null) ??
      '';
    setCopyDialogOpen(false);
    try {
      await navigator.clipboard.writeText(rawToken);
      showToast(t('tokenInspector.copy.success'), 'success');
      setFallbackToken(null);
    } catch {
      showToast(t('tokenInspector.copy.failure'), 'error');
      setFallbackToken(rawToken);
    }
  }, [pendingCopyToken, showToast, t]);

  const handleCopyClose = useCallback(() => {
    setCopyDialogOpen(false);
  }, []);

  const handleRefresh = useCallback(() => {
    void refetchCurrent();
  }, [refetchCurrent]);

  return (
    <div
      style={{ minHeight: '100vh', background: 'var(--color-surface-background)', fontFamily: 'var(--font-family-sans)' }}
    >
      {/* Breadcrumb / topbar */}
      <nav
        aria-label={t('common.breadcrumb')}
        style={{
          background: 'var(--color-surface-muted)',
          borderBottom: '1px solid var(--color-surface-border)',
          padding: 'var(--spacing-3) var(--spacing-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <a href="/_index" style={{ color: 'var(--color-text-secondary)', textDecoration: 'none' }} aria-label={t('common.homeAriaLabel')}>
          {t('common.home')}
        </a>
        <span style={{ color: 'var(--color-text-muted)' }} aria-hidden="true">›</span>
        <span>{t('tokenInspector.breadcrumb.devtools')}</span>
        <span style={{ color: 'var(--color-text-muted)' }} aria-hidden="true">›</span>
        <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }} aria-current="page">
          {t('tokenInspector.title')}
        </span>
        <span
          aria-label={t('tokenInspector.breadcrumb.adminBadge')}
          style={{
            display: 'inline-block',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            padding: '1px var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
            background: 'color-mix(in srgb, var(--color-danger) 15%, transparent)',
            color: 'var(--color-danger)',
            border: '1px solid var(--color-danger)',
            marginLeft: 'var(--spacing-2)',
          }}
        >
          ROLE_ADMIN
        </span>
      </nav>

      {/* Admin banner */}
      <div
        role="note"
        aria-label={t('tokenInspector.adminArea')}
        style={{
          background: 'color-mix(in srgb, var(--color-warning) 14%, var(--color-surface-muted))',
          borderBottom: '1px solid var(--color-warning)',
          padding: 'var(--spacing-2) var(--spacing-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-warning)',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 'var(--font-size-base)' }}>!</span>
        <span>{t('tokenInspector.adminBanner')}</span>
        <span
          style={{
            display: 'inline-block',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            padding: '1px var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-warning)',
            color: 'var(--color-text-inverse)',
            marginLeft: 'var(--spacing-2)',
          }}
        >
          ADMIN
        </span>
      </div>

      {/* Main content */}
      <main
        className="token-inspector-main"
        style={{ padding: 'var(--spacing-6)', maxWidth: '1200px', margin: '0 auto' }}
        aria-label={t('tokenInspector.title')}
      >
        {/* Page header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-4)',
            marginBottom: 'var(--spacing-6)',
            paddingBottom: 'var(--spacing-6)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0, marginBottom: 'var(--spacing-1)' }}>
              {t('tokenInspector.title')}
            </h1>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>
              {t('tokenInspector.subtitle')}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <button
              type="button"
              onClick={handleRefresh}
              aria-label={t('tokenInspector.refresh')}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
              }}
            >
              {t('tokenInspector.refresh')}
            </button>
            <button
              type="button"
              onClick={() => handleCopyRequest('')}
              aria-label={t('tokenInspector.copyToken')}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
              }}
            >
              {t('tokenInspector.copyToken')}
            </button>
          </div>
        </div>

        {/* Clipboard fallback textarea */}
        {fallbackToken !== null && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-4)',
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'color-mix(in srgb, var(--color-warning) 10%, var(--color-surface-muted))',
              border: '1px solid var(--color-warning)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <p style={{ margin: '0 0 var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)', fontWeight: 600 }}>
              {t('tokenInspector.copy.fallbackHint')}
            </p>
            <textarea
              readOnly
              value={fallbackToken}
              aria-label={t('tokenInspector.copy.fallbackAriaLabel')}
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
              rows={3}
              style={{
                width: '100%',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--spacing-2)',
                color: 'var(--color-text-primary)',
                resize: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {/* Help InfoCard */}
        <div style={{ marginBottom: 'var(--spacing-6)' }}>
          <InfoCard>
            <strong style={{ display: 'block', marginBottom: 'var(--spacing-1)', color: 'var(--color-text-primary)' }}>
              {t('tokenInspector.help.title')}
            </strong>
            <span>{t('tokenInspector.help.body')}</span>
            <ul
              style={{
                margin: 'var(--spacing-2) 0 0',
                paddingLeft: 'var(--spacing-4)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
              }}
            >
              <li>{t('tokenInspector.help.scenario.debug')}</li>
              <li>{t('tokenInspector.help.scenario.permission')}</li>
              <li>{t('tokenInspector.help.scenario.expiry')}</li>
            </ul>
          </InfoCard>
        </div>

        {/* 4-panel grid (2x2) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--spacing-4)',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <PanelA onCopyRequest={handleCopyRequest} />
          <PanelB />
          <PanelC />
          <PanelD />
        </div>

        {/* §6.7 Common Usage Panel */}
        <CommonUsagePanel
          items={COMMON_USAGE_ITEMS}
          defaultOpen={false}
          title={t('tokenInspector.commonUsage.title')}
        />
      </main>

      {/* Copy token confirmation dialog */}
      <CopyTokenDialog
        open={copyDialogOpen}
        onClose={handleCopyClose}
        onConfirm={handleCopyConfirm}
      />

      {/* Toast notifications */}
      <ToastContainer items={toastItems} onRemove={removeToast} />

      <style>{`
        @keyframes skeleton-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        /* code syntax tokens */
        .json-key    { color: var(--color-syntax-key); }
        .json-string { color: var(--color-syntax-string); }
        .json-number { color: var(--color-syntax-number); }
        .json-bool   { color: var(--color-syntax-bool); }
        .json-null   { color: var(--color-syntax-null); font-style: italic; }

        @media (max-width: 767px) {
          .token-inspector-main > div[style*="repeat(2, 1fr)"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

// ─── TokenInspectorPage ───────────────────────────────────────────────────────
// Auth / role enforcement is delegated entirely to AdminRouteGuard in router.tsx.

export function TokenInspectorPage() {
  return <TokenInspectorPageContent />;
}
